import React from 'react';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../../styled-components/dark';
import { Checkmark } from '../../assets/svg';

const STEP_DIM = 1.5; // em
const BORDER_PERCENT = 10; // %
const SIDE_MARGIN_BETWEEN_STEPS = STEP_DIM + 2 * (BORDER_PERCENT / 100) * STEP_DIM; // em
const TOTAL_STEP_DIM = STEP_DIM * (1 + BORDER_PERCENT / 100); // em

type WrapperType = {
  $width: number;
  $height: number;
};

const Wrapper = styled.div<WrapperType>`
  position: relative;
  padding: ${STEP_DIM * (BORDER_PERCENT / 100)}em;

  ${({ $width, $height }) => {
    return css`
      width: ${$width}em;
      height: ${$height}em;
    `;
  }}
`;

type StepRowType = {
  $index: number;
  $nbStepsByRow: number;
  $isNextCompleted: boolean;
};

const StepRow = styled.div<StepRowType>`
  position: absolute;

  ${({ $index, $nbStepsByRow }) => css`
    height: ${TOTAL_STEP_DIM}em;
    width: ${($nbStepsByRow - 1) * (STEP_DIM + SIDE_MARGIN_BETWEEN_STEPS + STEP_DIM * (BORDER_PERCENT / 100)) +
    TOTAL_STEP_DIM}em;

    left: ${STEP_DIM * (BORDER_PERCENT / 100)}em;
    top: ${STEP_DIM * (BORDER_PERCENT / 100) + $index * (TOTAL_STEP_DIM + SIDE_MARGIN_BETWEEN_STEPS)}em;
  `}

  &:not(:last-child) {
    &:after {
      content: '';
      position: absolute;

      width: ${STEP_DIM * 0.5}em;
      height: 0.1em;

      transition: background-color 300ms ease-in-out;
      background-color: ${({ $isNextCompleted }) =>
        $isNextCompleted
          ? theme.MissionDetailsAchievementProgress.completedBackgroundColor
          : theme.MissionDetailsAchievementProgress.defaultBackgroundColor};
    }

    &:nth-child(odd) {
      &:after {
        right: 0;
        transform-origin: top right;
        transform: translate(
            calc(-${STEP_DIM / 2}em - 25%),
            ${STEP_DIM + SIDE_MARGIN_BETWEEN_STEPS / 2 - STEP_DIM * 0.2}em
          )
          rotate(-90deg);
      }
    }

    &:nth-child(even) {
      &:after {
        left: 0;
        transform-origin: top left;
        transform: translate(
            ${STEP_DIM / 2 + ((BORDER_PERCENT / 100) * STEP_DIM) / 3}em,
            ${STEP_DIM + SIDE_MARGIN_BETWEEN_STEPS / 2 - STEP_DIM * 0.2}em
          )
          rotate(90deg);
      }
    }
  }
`;

type StepType = {
  $index: number;
  $isRowEven: boolean;
  $wrapperWidth: number;
  $isCompleted: boolean;
  $isNextCompleted: boolean;
};

const Step = styled.div<StepType>`
  position: absolute;

  width: ${STEP_DIM}em;
  height: ${STEP_DIM}em;
  border-radius: 50%;
  transform-style: preserve-3d;
  transform: translateZ(1px);

  &:not(:last-child) {
    &:after {
      content: '';
      position: absolute;

      left: 0;
      top: 50%;

      width: ${STEP_DIM * 0.5}em;
      height: 0.1em;
      background-color: ${theme.MissionDetailsAchievementProgress.defaultBackgroundColor};
    }
  }

  &:before {
    content: '';
    position: absolute;

    width: ${STEP_DIM * (1 + (BORDER_PERCENT * 2) / 100)}em;
    height: ${STEP_DIM * (1 + (BORDER_PERCENT * 2) / 100)}em;
    left: -${BORDER_PERCENT}%;
    top: -${BORDER_PERCENT}%;
    border-radius: 50%;

    transform-style: preserve-3d;
    transform: translateZ(-1px);
  }

  ${({ $index, $isRowEven, $wrapperWidth }) =>
    $isRowEven
      ? css`
          ${'' /* reverted */}

          left: ${$wrapperWidth - ($index + 0.52) * (TOTAL_STEP_DIM + SIDE_MARGIN_BETWEEN_STEPS)}em;
          &:not(:last-child) {
            &:after {
              transform: translate(${-(STEP_DIM - STEP_DIM * (BORDER_PERCENT / 100))}em, -50%);
            }
          }
        `
      : css`
          ${'' /* normal */}

          left: ${$index * (TOTAL_STEP_DIM + SIDE_MARGIN_BETWEEN_STEPS)}em;
          &:not(:last-child) {
            &:after {
              transform: translate(
                ${STEP_DIM + STEP_DIM * (BORDER_PERCENT / 100) + SIDE_MARGIN_BETWEEN_STEPS - 0.92 * STEP_DIM}em,
                -50%
              );
            }
          }
        `}

  ${({ $isCompleted, $isNextCompleted }) => css`
    transition: background-color 300ms ease-in-out 100ms;
    background-color: ${$isCompleted
      ? theme.MissionDetailsAchievementProgress.completedBackgroundColor
      : theme.MissionDetailsAchievementProgress.defaultBackgroundColor};

    &:before {
      transition: none 300ms ease-in-out 100ms;
      transition-property: box-shadow, background-color;
      will-change: box-shadow;

      ${$isCompleted
        ? css`
            background-color: ${theme.MissionDetailsAchievementProgress.completedBorderColor};
            box-shadow: 0px 0px 1em ${theme.MissionDetailsAchievementProgress.completedBorderColor};
          `
        : css`
            background-color: ${theme.MissionDetailsAchievementProgress.defaultBorderColor};
            box-shadow: 0px 0px 1em transparent;
          `}
    }

    &:not(:last-child) {
      &:after {
        transition: background-color 300ms ease-in-out;
        background-color: ${$isNextCompleted
          ? theme.MissionDetailsAchievementProgress.completedBackgroundColor
          : theme.MissionDetailsAchievementProgress.defaultBackgroundColor};
      }
    }
  `}
`;

type StyledCheckmarkType = {
  $show: boolean;
};

const StyledCheckmark = styled(Checkmark)<StyledCheckmarkType>`
  position: absolute;

  width: 220%;
  height: 220%;
  top: -60%;
  left: -60%;

  transition: none 300ms ease-in-out;
  transition-property: opacity, stroke-dashoffset;
  stroke-dasharray: 10;
  stroke-dashoffset: 10;

  ${({ $show }) => css`
    opacity: ${$show ? 1 : 0};
    stroke-dasharray: 10;

    ${$show
      ? css`
          stroke-dashoffset: 0;
        `
      : undefined}
  `}
`;

type MissionDetailsAchievementProgressProps = {
  className?: string;
  nbSteps?: number;
  nbCompleted?: number;
  maxColumns?: number;
};

const MissionDetailsAchievementProgress = ({
  className = '',
  nbSteps = 0,
  nbCompleted = 0,
  maxColumns = 1,
}: MissionDetailsAchievementProgressProps) => {
  /**
   * wrapperWidth and wrapperHeight are here to delimitate relative dimension of the container
   * to help the integration of the component, all the subtree is absolute rendered.
   *  */
  const wrapperWidth = useMemo(() => {
    return (
      Math.min(nbSteps - 1, maxColumns - 1) * (STEP_DIM + SIDE_MARGIN_BETWEEN_STEPS) +
      TOTAL_STEP_DIM +
      STEP_DIM * (BORDER_PERCENT / 100) * Math.min(nbSteps, maxColumns)
    );
  }, [maxColumns, nbSteps]);

  const wrapperHeight = useMemo(() => {
    return (
      (Math.ceil(nbSteps / maxColumns) - 1) *
        (STEP_DIM + SIDE_MARGIN_BETWEEN_STEPS + STEP_DIM * (BORDER_PERCENT / 100)) +
      TOTAL_STEP_DIM +
      STEP_DIM * (BORDER_PERCENT / 100)
    );
  }, [maxColumns, nbSteps]);

  const steps = useMemo(() => {
    const result = [];
    let resultIndex = 0;

    const full = [...new Array(nbSteps).keys()];

    for (let i = 0; i < nbSteps; i += maxColumns) {
      const subSteps = full.slice(i, i + maxColumns).map((fullIndex, index) => ({
        stepIndex: i + index,
        subStepIndex: index,
        isCompleted: fullIndex < nbCompleted,
        isNextCompleted: fullIndex + 1 < nbCompleted,
      }));
      result[resultIndex] = { isNextCompleted: i + maxColumns < nbCompleted, subSteps };
      resultIndex++;
    }

    return result;
  }, [maxColumns, nbCompleted, nbSteps]);

  const shouldRender = useMemo(() => !!nbSteps && steps, [nbSteps, steps]);

  return shouldRender ? (
    <Wrapper className={className} $width={wrapperWidth} $height={wrapperHeight}>
      {steps.map(({ subSteps, isNextCompleted }, index) => (
        <StepRow
          key={`step-row-${index}`}
          $index={index}
          $nbStepsByRow={subSteps.length}
          $isNextCompleted={isNextCompleted}
        >
          {subSteps.map(({ subStepIndex, isCompleted, isNextCompleted }) => (
            <Step
              key={`step-${subStepIndex}`}
              $index={subStepIndex}
              $isCompleted={isCompleted}
              $isNextCompleted={isNextCompleted}
              $isRowEven={(index + 1) % 2 === 0}
              $wrapperWidth={wrapperWidth}
            >
              <StyledCheckmark $show={isCompleted} />
            </Step>
          ))}
        </StepRow>
      ))}
    </Wrapper>
  ) : null;
};

export default MissionDetailsAchievementProgress;
